import type { Shape } from '@vd/annotation-core';
import type { StageImage } from './ImageCarousel';

/**
 * What the ticket's saved drawing is of: always its first image (the capture,
 * when it has one), with that image's own shapes, whichever image is on
 * screen (TOADO-716). The drawing is the ticket's card thumbnail and what an
 * agent reads as its annotated capture; drawn from the image on screen, it
 * became a picture of an added image filed under the capture's name.
 *
 * A ticket with no image at all draws on a blank slot, whose id is the empty
 * string (the canvas's active image id when it has none), so its drawing is
 * of the shapes that slot shows.
 */
export function rasterSource(images: StageImage[], shapes: Shape[], screenshotUrl?: string): {
  shapes: Shape[];
  imageUrl?: string;
  screenshotUrl?: string;
} {
  const first = images[0];
  const imageId = first?.id ?? '';
  return {
    shapes: shapes.filter((shape) => shape.imageId === imageId),
    imageUrl: first?.url,
    screenshotUrl: first ? undefined : screenshotUrl,
  };
}

/** Instance-owned ticket context. Separate canvases never share entries. */
export class AnnotationRasterContextRegistry<T extends object> {
  private readonly values = new Map<string, T>();

  set(ticketId: string, value: T): void {
    this.values.set(ticketId, value);
  }

  get(ticketId: string): T | undefined {
    return this.values.get(ticketId);
  }

  delete(ticketId: string, expected?: T): boolean {
    if (expected !== undefined && this.values.get(ticketId) !== expected) return false;
    return this.values.delete(ticketId);
  }
}
